<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Cenniki</span>
        <h3 class="page-title">Cenniki wzorcowe</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          class="text-nowrap"
          @click.prevent="$router.push('/cenniki/nowy')">
          <i class="material-icons">add</i>
          Dodaj cennik
        </d-button>
      </d-col>
    </d-row>

    <d-row
      class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="price-lists-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę cennika"
          v-model="filters.search" />
        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          :disabled="!loaded"
          @click.prevent="validateFiltersForm">Szukaj</d-button>
        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          :disabled="!loaded"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">
              Nazwa
            </th>
            <th
              scope="col"
              class="border-0 text-center">
              Aktualizacja
            </th>
            <th
              scope="col"
              class="border-0 text-right">
              Ilość produktów
            </th>
            <th
              scope="col"
              class="border-0 text-center"
              width="140">
              Akcje
            </th>
          </tr>
        </thead>
        <tbody v-if="loaded && !load_error && priceLists">
          <tr
            v-for="(priceList, index) of priceLists"
            :key="'price-list-' + index">
            <td class="align-middle">
              {{ priceList.name }}
            </td>
            <td class="align-middle text-center">
              {{ formatDate(priceList.updated_at) }}
            </td>
            <td class="align-middle text-right">
              {{ priceList.products_count }}
            </td>
            <td
              class="align-middle text-center d-flex justify-content-center"
              style="gap: 12px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/cenniki/edycja/' + priceList.id)"
                class="action-button"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                class="action-button"
                theme="danger"
                @click.native="removePriceList(priceList.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="priceLists.length === 0">
            <td
              colspan="4"
              class="text-center">
              Brak cenników do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <d-alert
        v-if="load_error"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a
          href="javascript:window.location.reload();"
        class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>

      <div
        v-else-if="!loaded"
        class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>
    </d-card>

    <pagination
      v-if="loaded && !load_error"
      :meta-data="paginationMetaData"
      :initial-page="currentPage"/>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/common/Pagination.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';

export default {
  name: 'price-lists',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data () {
    return {
      priceLists: [],
      loaded: false,
      load_error: false,
      removeInProgress: false,
      filters: {
        search: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };
      ListUtils.loadItemsData(this, {
        endpoint: '/price-lists/items?page=' + this.currentPage,
        listField: 'priceLists',
        errorKey: 'load_error',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.load_error = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['price-lists-filters-form'], this.debouncedLoadFilteredData);
    },
    removePriceList (id) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/price-lists/delete/',
        id,
        successTitle: 'Usunięto cennik',
        successText: 'Wybrany cennik został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten cennik?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie cennika nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    formatDate (date) {
      return CalendarUtils.formatDateForPHP(new Date(date));
    }
  }
};
</script>
